"use client";

import React from "react";
import { cn } from "@repo/ui/lib/utils";
import { useLocale } from "next-intl";

type PriceProps = {
  value: number;
  currency?: string;
  className?: string;
};

type Separators = { thousands: string; decimal: string };

const LOCALIZED_SEPARATORS: Record<string, Separators> = {
  "it-IT": { thousands: ".", decimal: "," },
  "en-GB": { thousands: ",", decimal: "." },
};

const DEFAULT_SEPARATORS: Separators = { thousands: ",", decimal: "." };

export function Price({ value, currency = "EUR", className = "" }: PriceProps) {
  const locale = useLocale();
  const separators = LOCALIZED_SEPARATORS[locale] ?? DEFAULT_SEPARATORS;

  const getCurrencyParts = (value: number) => {
    // en-US is necessary in order to retrieve integer part with thousands
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const parts = formatter.formatToParts(value);

    return {
      integer:
        parts
          .filter((p) => p.type === "integer")
          .map((p) => p.value)
          .join(separators.thousands) || "0",
      decimal: parts.find((p) => p.type === "fraction")?.value || "00",
      currency: parts.find((p) => p.type === "currency")?.value || "",
      decimalSeparator: separators.decimal,
    };
  };

  const parts = getCurrencyParts(value);

  return (
    <span
      className={cn(
        "text-[21px] max-md:text-[22px] relative inline-block",
        className,
      )}
      style={{ whiteSpace: "nowrap" }}
    >
      <span className="text-inherit">{parts.integer}</span>
      <sup className="text-[55%]">
        <span className="ml-[2px]">
          {parts.decimalSeparator}
          {parts.decimal}
        </span>
        <span className="ml-1">{parts.currency}</span>
      </sup>
    </span>
  );
}
